import React, { useState, useEffect } from "react";
import SecondLayout from "../layouts/SecondLayout";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { Variables } from "../variables";

const Business = () => {
  const navigate = useNavigate();

  const [language, setLanguage] = useState(Cookies.get("RashaLang") || "en");
  const [content, setContent] = useState([]);

  // loader + error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // scroll to top & poll language cookie
  useEffect(() => {
    window.scrollTo(0, 0);
    const interval = setInterval(() => {
      const currentLang = Cookies.get("RashaLang") || "en";
      setLanguage((prev) => (prev !== currentLang ? currentLang : prev));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // fetch content (show loader without layout, show layout after)
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const url =
          Variables.API_URL +
          "Rasha/Client/Page/3?isEnglish=" +
          (language === "en");

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.message || "Failed to fetch content");
        }

        if (isMounted) setContent(Array.isArray(result) ? result : []);
      } catch (err) {
        if (isMounted && err.name !== "AbortError") {
          setError(err.message || "Something went wrong");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [language]);

  const isArabic = language === "ar";

  // ----- Loader without layout -----
  if (loading) {
    return (
      <div style={styles.loaderWrap}>
        <div style={styles.spinner} />
      </div>
    );
  }

  // ----- Error without layout (optional) -----
  if (error) {
    return <div style={styles.errorBox}>{error}</div>;
  }

  // ----- Main UI with layout (only after loading finishes) -----
  return (
    <SecondLayout>
      <div className="container">
        <h2 className="about-me-title text-center">{content?.[0]?.WidgetName}</h2>

        <div className="opportunity-section">
          {content.map((c, index) => {
            const key = c.WidgetId ?? `${c.WidgetTypeId}-${c.WidgetValue?.Id ?? index}`;
            const imgSrc = Variables.API_PUBLISHED + (c?.WidgetValue?.ImageUrl || "");
            const title = language === "en" ? c?.WidgetValue?.Title : c?.WidgetValue?.TitleAr;
            const body = language === "en" ? c?.WidgetValue?.Body : c?.WidgetValue?.BodyAr;
            const btnText =
              language === "en" ? c?.WidgetValue?.ButtonText : c?.WidgetValue?.ButtonTextAr;
            const btnUrl = c?.WidgetValue?.ButtonUrl || "#";

            if (index % 2 === 0) {
              return (
                <div key={key} className={`opportunity-row ${isArabic ? "reverse" : ""}`}>
                  <div className="opportunity-img">
                    <img src={imgSrc} alt={title || "Item"} />
                  </div>
                  <div className="opportunity-text light-bg">
                    <h3>{title}</h3>
                    <p>{body}</p>
                    <button className="cta-btn light" onClick={() => navigate(btnUrl)}>
                      {btnText}
                    </button>
                  </div>
                </div>
              );
            } else {
              return (
                <div key={key} className={`opportunity-row ${!isArabic ? "reverse" : ""}`}>
                  <div className="opportunity-img">
                    <img src={imgSrc} alt={title || "Item"} />
                  </div>
                  <div className="opportunity-text dark-bg">
                    <h3>{title}</h3>
                    <p>{body}</p>
                    <button className="cta-btn light" onClick={() => navigate(btnUrl)}>
                      {btnText}
                    </button>
                  </div>
                </div>
              );
            }
          })}
        </div>
      </div>
    </SecondLayout>
  );
};

const styles = {
  loaderWrap: {
    minHeight: "60vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: 48,
    height: 48,
    border: "4px solid rgba(0,0,0,0.1)",
    borderTopColor: "#000",
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  },
  errorBox: {
    margin: 16,
    padding: "12px 16px",
    border: "1px solid #f3c2c2",
    background: "#fff5f5",
    color: "#b00020",
    borderRadius: 8,
  },
};

// inject keyframes (once)
if (typeof document !== "undefined" && !document.getElementById("biz-spin-kf")) {
  const styleEl = document.createElement("style");
  styleEl.id = "biz-spin-kf";
  styleEl.innerHTML = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);
}

export default Business;
